import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Plus, Search, Phone, PiggyBank } from "lucide-react";
import { formatINR } from "../lib/format";

function CustomerRow({ c }) {
    const [dx, setDx] = useState(0);
    const [startX, setStartX] = useState(null);

    function onTouchStart(e) {
        setStartX(e.touches[0].clientX);
    }
    function onTouchMove(e) {
        if (startX == null) return;
        const next = e.touches[0].clientX - startX;
        // clamp so the card snaps within ±90px
        setDx(Math.max(-90, Math.min(90, next)));
    }
    function onTouchEnd() {
        if (dx > 60) {
            // right swipe → call
            window.location.href = `tel:${c.mobile}`;
        } else if (dx < -60) {
            // left swipe → SMS
            window.location.href = `sms:${c.mobile}`;
        }
        setDx(0);
        setStartX(null);
    }

    return (
        <div
            className="relative bg-white overflow-hidden select-none"
            data-testid={`customer-row-${c.id}`}
        >
            {/* Underlay action backgrounds */}
            <div className="absolute inset-y-0 left-0 flex items-center px-4 bg-blue-900 text-white text-sm font-semibold">
                <Phone className="h-4 w-4 mr-1.5" /> Call
            </div>
            <div className="absolute inset-y-0 right-0 flex items-center px-4 bg-emerald-600 text-white text-sm font-semibold">
                SMS
            </div>
            <Link
                to={`/customers/${c.id}`}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                className="block px-5 py-4 hover:bg-slate-50 transition relative bg-white"
                style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? "transform 0.2s ease" : "none" }}
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <div className="font-semibold text-slate-900 truncate uppercase">
                                {c.name}
                            </div>
                            <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {c.customer_code}
                            </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                            {c.occupation || "—"} · {c.address || "—"}
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.location.href = `tel:${c.mobile}`;
                            }}
                            className="inline-flex items-center gap-1 text-blue-900 text-sm font-semibold"
                            data-testid={`call-${c.id}`}
                        >
                            <Phone className="h-3.5 w-3.5" />
                            {c.mobile}
                        </button>
                        {c.active_loan_principal > 0 ? (
                            <div
                                className="text-[11px] text-blue-900 font-bold mt-0.5"
                                data-testid={`active-principal-${c.id}`}
                            >
                                Active loan: {formatINR(c.active_loan_principal)}
                            </div>
                        ) : (
                            <div className="text-[11px] text-slate-400 mt-0.5">No active loan</div>
                        )}
                    </div>
                </div>
            </Link>
        </div>
    );
}

function FilterChip({ value, label, count, active, onClick }) {
    return (
        <button
            type="button"
            data-testid={`cust-filter-${value}`}
            aria-pressed={active}
            onClick={onClick}
            className={`px-3.5 h-9 rounded-full text-xs font-semibold border transition tap ${
                active
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
        >
            {label}
            <span className={`ml-1.5 text-[10px] font-mono ${
                active ? "text-blue-100" : "text-slate-400"
            }`}>
                {count ?? 0}
            </span>
        </button>
    );
}

export default function Customers() {
    const { t } = useLang();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [q, setQ] = useState("");
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("all"); // all | loan | pigmy | both

    async function load() {
        const { data } = await api.get("/customers", { params: q ? { q } : {} });
        setList(data);
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [q]);

    const { withLoans, pigmyOnly } = useMemo(() => {
        const hasLoan = (c) => (c.active_loan_count || 0) > 0;
        const hasPigmy = (c) => (c.pigmy_account_count || 0) > 0;
        const filtered = list.filter((c) => {
            if (filter === "loan") return hasLoan(c) && !hasPigmy(c);
            if (filter === "pigmy") return hasPigmy(c) && !hasLoan(c);
            if (filter === "both") return hasLoan(c) && hasPigmy(c);
            return true;
        });
        const wl = [];
        const po = [];
        for (const c of filtered) {
            if (c.pigmy_only) po.push(c);
            else wl.push(c);
        }
        return { withLoans: wl, pigmyOnly: po };
    }, [list, filter]);

    const counts = useMemo(() => {
        const hasLoan = (c) => (c.active_loan_count || 0) > 0;
        const hasPigmy = (c) => (c.pigmy_account_count || 0) > 0;
        return {
            all: list.length,
            loan: list.filter((c) => hasLoan(c) && !hasPigmy(c)).length,
            pigmy: list.filter((c) => hasPigmy(c) && !hasLoan(c)).length,
            both: list.filter((c) => hasLoan(c) && hasPigmy(c)).length,
        };
    }, [list]);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    {t("customers")}
                </h1>
                {isAdmin && (
                    <Link to="/customers/new">
                        <Button
                            data-testid="add-customer-btn"
                            className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap"
                        >
                            <Plus className="h-4 w-4 mr-1.5" />
                            {t("add_customer")}
                        </Button>
                    </Link>
                )}
            </div>

            <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                    data-testid="customer-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("search_placeholder")}
                    className="pl-10 h-12 rounded-xl bg-white"
                />
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-testid="customer-filter-bar">
                <FilterChip value="all" label="All" count={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
                <FilterChip value="loan" label="Loan only" count={counts.loan} active={filter === "loan"} onClick={() => setFilter("loan")} />
                <FilterChip value="pigmy" label="Saving Scheme only" count={counts.pigmy} active={filter === "pigmy"} onClick={() => setFilter("pigmy")} />
                <FilterChip value="both" label="Loan + Saving Scheme" count={counts.both} active={filter === "both"} onClick={() => setFilter("both")} />
            </div>

            <Card className="rounded-2xl border-slate-100 overflow-hidden">
                {list.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">{t("no_data")}</div>
                ) : withLoans.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">
                        No customers with active loans
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {withLoans.map((c) => (
                            <CustomerRow key={c.id} c={c} />
                        ))}
                    </div>
                )}
            </Card>

            {pigmyOnly.length > 0 && (
                <div className="space-y-2" data-testid="pigmy-only-section">
                    <div className="flex items-center gap-2 px-1">
                        <PiggyBank className="h-4 w-4 text-amber-700" />
                        <h2 className="text-[11px] uppercase tracking-wider font-bold text-amber-800">
                            Saving Scheme-only Customers ({pigmyOnly.length})
                        </h2>
                    </div>
                    <Card className="rounded-2xl border-amber-100 bg-amber-50/30 overflow-hidden">
                        <div className="divide-y divide-amber-100">
                            {pigmyOnly.map((c) => (
                                <CustomerRow key={c.id} c={c} />
                            ))}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
